<template>
  <div>
    <div v-if="isDownloading" class="floating-pdf-toast">
      <i class="fa-solid fa-circle-notch fa-spin me-2"></i> 
      Generating PDF: {{ pdfData?.proposal_title || 'Proposal' }}...
    </div>

    <div v-if="pdfData" id="pdf-content-area" :style="pdfContentAreaStyle">
      <div class="pdf-page-section">
        <h2 class="pdf-title" style="font-size: 24pt; text-align: center; margin-bottom: 1.5em; color: #00a0c1;">
          {{ pdfData.proposal_title }}
        </h2>
      </div>
      
      <div v-if="pdfData.cover_page_content" class="pdf-page-section raw-html-section" v-html="pdfData.cover_page_content"></div>
      <div v-if="pdfData.product_service_content" class="pdf-page-section raw-html-section" v-html="pdfData.product_service_content"></div>
      
      <div v-if="pdfData.filteredProposalItems && pdfData.filteredProposalItems.length > 0" class="pdf-page-section pdf-pricing-section">
        <div v-for="(pi, optionIndex) in pdfData.filteredProposalItems" :key="'pdf-option-' + pi.option_index">
          <h4 class="pdf-section-title" style="font-size: 14pt; border-bottom: 2px solid #00a0c1; padding-bottom: 5px; margin-bottom: 1em; color: #00a0c1;">
            Vertex Products - {{ (pi.option_index == 1) ? 'Main Option' : 'Option ' + toOptionName(pi.option_index) }}
          </h4>
          <table class="pdf-product-table" style="width: 100%; border-collapse: collapse; margin: 20px 0; border: 1px solid #cccccc;">
            <thead>
              <tr>
                <th style="background-color: #00a0c1; color: white; padding: 8px; border: 1px solid #e0e0e0;">Product</th>
                <th style="background-color: #00a0c1; color: white; padding: 8px; border: 1px solid #e0e0e0;">Description</th>
                <th style="background-color: #00a0c1; color: white; padding: 8px; border: 1px solid #e0e0e0;">Notes</th>
                <th style="background-color: #00a0c1; color: white; padding: 8px; border: 1px solid #e0e0e0;">Qty</th>
                <th style="background-color: #00a0c1; color: white; padding: 8px; border: 1px solid #e0e0e0;">Price</th>
                <th v-if="pdfData.show_discount_calculation" style="background-color: #00a0c1; color: white; padding: 8px; border: 1px solid #e0e0e0;">Price Discount</th>
                <th v-if="pdfData.show_discount_calculation" style="background-color: #00a0c1; color: white; padding: 8px; border: 1px solid #e0e0e0;">Total Discount</th>
                <th style="background-color: #00a0c1; color: white; padding: 8px; border: 1px solid #e0e0e0;">Total AUD</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, itemIndex) in pi.productList" :key="'pdf-item-' + pi.option_index + '-' + itemIndex">
                <td style="padding: 8px; border: 1px solid #e0e0e0;"><strong>{{ item.Product?.display_name || "none" }}</strong></td>
                <td style="padding: 8px; border: 1px solid #e0e0e0;">{{ item.description }}</td>
                <td style="padding: 8px; border: 1px solid #e0e0e0;">{{ item.remarks }}</td>
                <td style="padding: 8px; border: 1px solid #e0e0e0;"><span>{{ item.quantity }}</span></td>
                <td style="padding: 8px; border: 1px solid #e0e0e0;">
                  <span v-if="!pdfData.show_discount_calculation">{{ formatCurrency(item.price - item.discount) }}</span>
                  <span v-else>{{ formatCurrency(item.price) }}</span>
                </td>
                <td v-if="pdfData.show_discount_calculation" style="padding: 8px; border: 1px solid #e0e0e0;"><span>{{ formatCurrency(item.discount) }}</span></td>
                <td v-if="pdfData.show_discount_calculation" style="padding: 8px; border: 1px solid #e0e0e0;"><span>{{ formatCurrency(item.discount * item.quantity) }}</span></td>
                <td style="padding: 8px; border: 1px solid #e0e0e0;">{{ formatCurrency(calculateTotal(item)) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr style="background-color: #f5f5f5; font-weight: bold;">
                <td :colspan="pdfData.show_discount_calculation ? 7 : 5" style="text-align: right; padding: 8px 15px; border: 1px solid #e0e0e0;">Subtotal</td>
                <td style="text-align: right; padding: 8px; border: 1px solid #e0e0e0;">{{ formatCurrency(calculateOptionSubtotalPrice(pi.productList)) }}</td>
              </tr>
              <tr style="background-color: #f5f5f5; font-weight: bold;">
                <td :colspan="pdfData.show_discount_calculation ? 7 : 5" style="text-align: right; padding: 8px 15px; border: 1px solid #e0e0e0;">GST (10%)</td>
                <td style="text-align: right; padding: 8px; border: 1px solid #e0e0e0;">{{ formatCurrency(calculateOptionGST(pi.productList)) }}</td>
              </tr>
              <tr style="background-color: #f5f5f5; font-weight: bold;">
                <td :colspan="pdfData.show_discount_calculation ? 7 : 5" style="text-align: right; padding: 8px 15px; border: 1px solid #e0e0e0;">Total</td>
                <td style="text-align: right; padding: 8px; border: 1px solid #e0e0e0;">{{ formatCurrency(calculateOptionTotalPrice(pi.productList)) }}</td>
              </tr>
            </tfoot>
          </table>
          <br v-if="optionIndex < pdfData.filteredProposalItems.length - 1" />
        </div>
      </div>

      <div v-if="pdfData.content_conflict" class="pdf-page-section raw-html-section" v-html="pdfData.content_conflict"></div>
      <div v-if="pdfData.content_insurance" class="pdf-page-section raw-html-section" v-html="pdfData.content_insurance"></div>
      <div v-if="pdfData.content_whs_responsibilities" class="pdf-page-section raw-html-section" v-html="pdfData.content_whs_responsibilities"></div>
      <div v-if="pdfData.content_terms_conditions" class="pdf-page-section raw-html-section" v-html="pdfData.content_terms_conditions"></div>
    </div>
  </div>
</template>

<script>
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export default {
  name: 'GlobalPdfGenerator',
  data() {
    return {
      isDownloading: false,
      pdfContentAreaStyle: {
        position: 'absolute',
        left: '-9999px',
        top: 0,
        width: '800px', // Fixed width to ensure WYSIWYG text wraps predictably
        padding: '20px',
        backgroundColor: 'white',
        boxSizing: 'border-box',
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontSize: '11pt',
        lineHeight: '1.5',
        color: '#333333'
      }
    }
  },
  computed: {
    pdfData() {
      return this.$store?.state?.pdf?.pdfPayload;
    }
  },
  watch: {
    pdfData(newData) {
      if (newData) {
        this.generatePDF();
      }
    }
  },
  methods: {
    calculateTotal(item) { return (parseFloat(item.price || 0) - parseFloat(item.discount || 0)) * parseInt(item.quantity || 0); },
    calculateOptionSubtotalPrice(productList) { return productList.reduce((total, item) => total + this.calculateTotal(item), 0); },
    calculateOptionGST(productList) { return this.calculateOptionSubtotalPrice(productList) * 0.1; },
    calculateOptionTotalPrice(productList) { return this.calculateOptionSubtotalPrice(productList) * 1.1; },
    formatCurrency(value) { return isNaN(parseFloat(value)) ? '$0.00' : `$${parseFloat(value).toFixed(2)}`; },
    toOptionName(count) {
      if (count <= 1) return '';
      let result = '', num = count;
      while (num > 0) {
        let remainder = num % 26;
        if (remainder === 0) remainder = 26;
        result = String.fromCharCode(64 + remainder) + result;
        num = Math.floor((num - remainder) / 26);
      }
      return result;
    },

    async generatePDF() {
      this.isDownloading = true;
      await this.$nextTick(); 

      setTimeout(async () => {
        try {
          const pdfContentElement = document.getElementById('pdf-content-area');
          if (!pdfContentElement) {
            this.$toast.error("PDF generation failed.");
            this.isDownloading = false;
            return;
          }

          const pdf = new jsPDF('p', 'mm', 'a4');
          const A4_WIDTH = 210, A4_HEIGHT = 297, PAGE_MARGIN = 20, CONTENT_WIDTH = A4_WIDTH - (PAGE_MARGIN * 2);
          let cursorY = PAGE_MARGIN;
          const DOM_RATIO = CONTENT_WIDTH / pdfContentElement.offsetWidth;

          // Core Renderer: Moves elements to next page if they don't fit
          const renderElement = async (element, bottomMargin = 0) => {
            if (!element || element.clientHeight === 0) return;
            
            // JPEGs and Scale 2 drastically reduce file size from 95MB to ~2MB!
            const canvas = await html2canvas(element, { scale: 2, useCORS: true, logging: false });
            const imgHeightMM = canvas.height * (CONTENT_WIDTH / canvas.width);

            // If it exceeds the page, push to a new page. NO SLICING ALLOWED!
            if (cursorY + imgHeightMM > A4_HEIGHT - PAGE_MARGIN) {
              pdf.addPage();
              cursorY = PAGE_MARGIN;
            }

            // Draw image using JPEG compression
            pdf.addImage(canvas.toDataURL('image/jpeg', 0.8), 'JPEG', PAGE_MARGIN, cursorY, CONTENT_WIDTH, imgHeightMM);
            cursorY += imgHeightMM + bottomMargin;
          };

          // Process the layout block by block
          const sections = Array.from(pdfContentElement.querySelectorAll('.pdf-page-section'));
          
          for (const section of sections) {
            if (section.clientHeight === 0) continue;

            // Handle Tables row by row so they don't break in half
            if (section.classList.contains('pdf-pricing-section')) {
              const optionBlocks = section.querySelectorAll(':scope > div');
              
              for (const optionBlock of optionBlocks) {
                await renderElement(optionBlock.querySelector('h4'), 3);
                const table = optionBlock.querySelector('table.pdf-product-table');
                
                if (table) {
                  // Lock column widths to prevent snapping
                  const ths = Array.from(table.querySelectorAll('th'));
                  const colWidths = ths.map(th => window.getComputedStyle(th).width);
                  ths.forEach((th, i) => th.style.width = th.style.minWidth = th.style.maxWidth = colWidths[i]);
                  Array.from(table.querySelectorAll('tbody > tr')).forEach(row => {
                    Array.from(row.querySelectorAll('td')).forEach((td, i) => { if(colWidths[i]) td.style.width = td.style.minWidth = td.style.maxWidth = colWidths[i]; });
                  });

                  // Render row chunk
                  const renderTableChunk = async (chunkElement, isRow = false) => {
                    const tempTable = document.createElement('table');
                    tempTable.className = table.className;
                    tempTable.style.cssText = table.style.cssText;
                    tempTable.style.width = '100%';
                    tempTable.style.borderCollapse = 'collapse';
                    tempTable.appendChild(chunkElement.cloneNode(true));
                    
                    const tempDiv = document.createElement('div');
                    tempDiv.appendChild(tempTable);
                    table.parentNode.insertBefore(tempDiv, table);
                    
                    // Fast height check using raw DOM math instead of html2canvas
                    const estimatedMM = tempDiv.offsetHeight * DOM_RATIO;
                    
                    // If this row breaks the page, add a new page AND reprint the header
                    if (cursorY + estimatedMM > A4_HEIGHT - PAGE_MARGIN && isRow) {
                       pdf.addPage();
                       cursorY = PAGE_MARGIN;
                       
                       const thead = table.querySelector('thead');
                       if (thead) {
                          const headerWrapper = document.createElement('table');
                          headerWrapper.style.cssText = table.style.cssText;
                          headerWrapper.style.width = '100%';
                          headerWrapper.style.borderCollapse = 'collapse';
                          headerWrapper.appendChild(thead.cloneNode(true));
                          const hdDiv = document.createElement('div');
                          hdDiv.appendChild(headerWrapper);
                          table.parentNode.insertBefore(hdDiv, table);
                          await renderElement(hdDiv, 0);
                          hdDiv.remove();
                       }
                    }

                    await renderElement(tempDiv, 0);
                    tempDiv.remove();
                  };

                  if (table.querySelector('thead')) await renderTableChunk(table.querySelector('thead'), false);
                  for (const row of Array.from(table.querySelectorAll('tbody > tr'))) {
                    await renderTableChunk(row, true);
                  }
                  if (table.querySelector('tfoot')) await renderTableChunk(table.querySelector('tfoot'), true);
                }
                cursorY += 10;
              }
            } 
            // Handle Raw HTML (WYSIWYG content) paragraph by paragraph so text isn't cut
            else if (section.classList.contains('raw-html-section')) {
              const children = Array.from(section.children);
              if (children.length === 0) {
                 await renderElement(section, 5); 
              } else {
                 for (const child of children) {
                    // Skip empty paragraphs that might cause awkward spacing
                    if (child.tagName === 'P' && child.innerHTML.trim() === '') {
                        cursorY += 2;
                        continue;
                    }
                    await renderElement(child, 3);
                 }
              }
            }
            // Fallback for Title
            else {
              await renderElement(section, 5);
            }
          }

          // Generate Headers, Footers, and Draft Watermarks
          const totalPages = pdf.getNumberOfPages();
          for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i);
            
            if (this.pdfData.proposalStatus === null || this.pdfData.proposalStatus === 0) {
              pdf.saveGraphicsState();
              pdf.setGState(new pdf.GState({opacity: 0.15}));
              pdf.setTextColor(173, 216, 230);
              pdf.setFontSize(100);
              pdf.setFont('helvetica', 'bold');
              pdf.text("DRAFT", A4_WIDTH / 2, A4_HEIGHT / 2, { angle: 45, align: "center" });
              pdf.restoreGraphicsState();
            }

            pdf.setFontSize(8);
            pdf.setTextColor("#666666");
            const footerText = `${this.pdfData.proposal_title || 'Proposal'} - Page ${i} of ${totalPages}`;
            const textWidth = pdf.getStringUnitWidth(footerText) * pdf.getFontSize() / pdf.internal.scaleFactor;
            pdf.text(footerText, (A4_WIDTH - textWidth) / 2, A4_HEIGHT - (PAGE_MARGIN / 2));
          }

          const now = new Date();
          const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
          const sanitizedTitle = (this.pdfData.proposal_title || 'Proposal').replace(/[^a-z0-9\s]/gi, '_').replace(/\s+/g, '_');
          
          pdf.save(`${sanitizedTitle}_${timestamp}.pdf`);
          this.$toast.success("PDF Downloaded Successfully!");
        } catch (error) {
          console.error("PDF Generation failed:", error);
          this.$toast.error("PDF generation failed. Please stay on the page while downloading.");
        } finally {
          this.isDownloading = false;
          this.$store.commit('pdf/CLEAR_PDF_DOWNLOAD');
        }
      }, 1000); 
    }
  }
}
</script>

<style scoped>
.floating-pdf-toast {
  position: fixed;
  bottom: 30px;
  right: 30px;
  background-color: #00A0C1; 
  color: white;
  padding: 16px 24px;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.2);
  z-index: 10000;
  font-weight: 600;
  display: flex;
  align-items: center;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from { transform: translateY(100%); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

/* Explicit fallback styles to protect against Vue Router CSS destruction */
.raw-html-section h1, .raw-html-section h2, .raw-html-section h3, .raw-html-section h4 { color: #00a0c1 !important; margin-bottom: 15px !important; }
.raw-html-section p { margin-bottom: 10px !important; }
.raw-html-section img { max-width: 100% !important; height: auto !important; }
</style>